/**
 * ============================================================================
 * ImpactSetReview — Phase 6c frontend (CCIE — Change Control Impact Engine).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /changes/:id/impact-set
 * Purpose: surfaces the computed Impact Set for a CC. The user reads through
 *          each category (DOCUMENT, TRAINING_TASK, EQUIPMENT, …), reviews the
 *          confidence + discovery rule, and either acknowledges, approves, or
 *          rejects the proposal.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Editorial layout: main content + sticky side-panel summary.
 *   - HIGH-confidence sections expanded by default; MEDIUM/LOW collapsed
 *     with a single "Expand weaker inferences (N)" button per CCIE-CONF-001.
 *   - Approve button only appears for bulk CCs (item_count > 50); it opens
 *     the dual-sign approve dialog. For non-bulk CCs the single Acknowledge
 *     button is the primary action.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './ImpactSetReview.module.css';
import {
  useImpactSetQuery,
  type Confidence,
  type ImpactCategory,
  type ImpactItem,
} from './useImpactSetQuery';
import { CategorySection } from './CategorySection';
import { AcknowledgeDialog } from './AcknowledgeDialog';
import { ApproveDialog } from './ApproveDialog';
import { RejectDialog } from './RejectDialog';
import { RelatedRecords } from '../../components/RelatedRecords';

const CATEGORY_ORDER: ImpactCategory[] = [
  'DOCUMENT',
  'TRAINING_TASK',
  'EQUIPMENT',
  'SUPPLIER',
  'PRODUCT',
  'REGULATORY_ANCHOR',
  'VALIDATION',
];

const BULK_THRESHOLD = 50;

type DialogState =
  | { kind: 'closed' }
  | { kind: 'acknowledge' }
  | { kind: 'approve' }
  | { kind: 'reject' };

export interface ImpactSetReviewProps {
  /** Optional override so the screen renders standalone without a router. */
  changeId?: string;
}

export function ImpactSetReview({ changeId = 'cc-demo' }: ImpactSetReviewProps = {}) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  const impactQ = useImpactSetQuery(changeId);
  const data = impactQ.data;

  const [showWeaker, setShowWeaker] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });

  // Group items by category so each section gets a stable list.
  const itemsByCategory: Record<ImpactCategory, ImpactItem[]> = useMemo(() => {
    const empty: Record<ImpactCategory, ImpactItem[]> = {
      DOCUMENT: [],
      TRAINING_TASK: [],
      EQUIPMENT: [],
      SUPPLIER: [],
      PRODUCT: [],
      REGULATORY_ANCHOR: [],
      VALIDATION: [],
    };
    if (!data) return empty;
    for (const it of data.items) {
      const bucket = empty[it.category];
      if (bucket) bucket.push(it);
    }
    return empty;
  }, [data]);

  const totalItems = data?.summary.total_items ?? 0;
  const weakerCount = useMemo(() => {
    if (!data) return 0;
    return (
      (data.summary.by_confidence.MEDIUM ?? 0) +
      (data.summary.by_confidence.LOW ?? 0)
    );
  }, [data]);

  const isBulk = totalItems > BULK_THRESHOLD;
  const canAck = data?.status === 'PROPOSED';
  const canApprove = data?.status === 'ACKNOWLEDGED';
  const canReject =
    data?.status === 'PROPOSED' || data?.status === 'ACKNOWLEDGED';

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('changeControl.impactSet.eyebrow', {
          defaultValue: 'Quality Events',
        })}
        title={t('changeControl.impactSet.title', {
          defaultValue: 'Impact set review',
        })}
        subtitle={
          data
            ? t('changeControl.impactSet.subtitleWithChange', {
                defaultValue: '{{title}} · {{subject}}',
                title: data.title,
                subject: data.subject_entity_display_name,
              })
            : t('changeControl.impactSet.subtitle', {
                defaultValue:
                  'Review every impacted entity before acknowledging or approving the change.',
              })
        }
        actions={
          <div className={styles.headerActions}>
            {canReject && (
            <Button
              variant="tertiary"
              onClick={() => setDialog({ kind: 'reject' })}
              aria-label={t('changeControl.impactSet.actions.rejectAria', {
                defaultValue: 'Reject this change control',
              })}
            >
              {t('changeControl.impactSet.actions.reject', {
                defaultValue: 'Reject',
              })}
            </Button>
          )}
          {canAck && (
            <Button
              variant="primary"
              onClick={() => setDialog({ kind: 'acknowledge' })}
              aria-label={t('changeControl.impactSet.actions.ackAria', {
                defaultValue: 'Acknowledge the impact set',
              })}
            >
              {t('changeControl.impactSet.actions.acknowledge', {
                defaultValue: 'Acknowledge',
              })}
            </Button>
          )}
          {canApprove && (
            <Button
              variant="primary"
              onClick={() => setDialog({ kind: 'approve' })}
              aria-label={
                isBulk
                  ? t('changeControl.impactSet.actions.approveBulkAria', {
                      defaultValue:
                        'Approve the change control — bulk approval requires two signers',
                    })
                  : t('changeControl.impactSet.actions.approveAria', {
                      defaultValue: 'Approve the change control',
                    })
              }
            >
              {t('changeControl.impactSet.actions.approve', {
                defaultValue: 'Approve',
              })}
            </Button>
          )}
          </div>
        }
      />

      {impactQ.isLoading && (
        <Card>
          <CardBody>
            <div role="status" aria-live="polite">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {impactQ.isError && (
        <Card>
          <CardBody>
            <div role="alert" className={styles.errorBanner}>
              <span>
                {t('changeControl.impactSet.errors.loadFailed', {
                  defaultValue: 'Could not load the impact set.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => impactQ.refetch()}
              >
                {t('changeControl.impactSet.actions.retry', {
                  defaultValue: 'Retry',
                })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {!impactQ.isLoading && !impactQ.isError && data && (
        <div className={styles.layout}>
          <main className={styles.mainCol}>
            <Card>
              <CardHeader>
                <div className={styles.cardHeaderRow}>
                  <h2 className={wstyles.sectionTitle}>
                    {t('changeControl.impactSet.sections.title', {
                      defaultValue: 'Impacted entities',
                    })}{' '}
                    <span className={styles.muted}>
                      ({nf.format(totalItems)})
                    </span>
                  </h2>
                  <span className={styles.computedAt}>
                    {t('changeControl.impactSet.computedAt', {
                      defaultValue: 'Computed {{ts}} · {{ms}} ms',
                      ts: dateFmt.format(new Date(data.computed_at_utc)),
                      ms: nf.format(data.compute_duration_ms),
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardBody>
                {totalItems === 0 ? (
                  <p className={styles.emptyState}>
                    {t('changeControl.impactSet.empty', {
                      defaultValue:
                        'The impact engine found no downstream entities. The change is fully self-contained.',
                    })}
                  </p>
                ) : (
                  <div className={styles.categoriesList}>
                    {CATEGORY_ORDER.map((cat) => {
                      const items = itemsByCategory[cat];
                      if (items.length === 0) return null;
                      // HIGH-confidence sections open by default; others open
                      // only after the user clicks "Expand weaker inferences".
                      const anyHigh = items.some((i) => i.confidence === 'HIGH');
                      const open = anyHigh || showWeaker;
                      return (
                        <CategorySection
                          key={cat}
                          category={cat}
                          items={items}
                          defaultOpen={open}
                        />
                      );
                    })}
                  </div>
                )}

                {!showWeaker && weakerCount > 0 && (
                  <Button
                    variant="tertiary"
                    onClick={() => setShowWeaker(true)}
                    aria-label={t(
                      'changeControl.impactSet.actions.expandWeakerAria',
                      {
                        defaultValue:
                          'Expand {{n}} weaker MEDIUM and LOW confidence inferences',
                        n: weakerCount,
                      },
                    )}
                  >
                    {t('changeControl.impactSet.actions.expandWeaker', {
                      defaultValue: 'Expand weaker inferences ({{n}})',
                      n: nf.format(weakerCount),
                    })}
                  </Button>
                )}
              </CardBody>
            </Card>
          </main>

          <aside
            className={styles.sideCol}
            aria-label={t('changeControl.impactSet.sideAria', {
              defaultValue: 'Impact set summary',
            })}
          >
            <Card>
              <CardHeader>
                <h2 className={wstyles.sectionTitle}>
                  {t('changeControl.impactSet.summary.title', {
                    defaultValue: 'Summary',
                  })}
                </h2>
              </CardHeader>
              <CardBody>
                <div className={styles.summaryStat}>
                  <span className={styles.summaryStatLabel}>
                    {t('changeControl.impactSet.summary.total', {
                      defaultValue: 'Total impact items',
                    })}
                  </span>
                  <span className={styles.summaryStatValue}>
                    {nf.format(totalItems)}
                  </span>
                </div>

                <h3 className={styles.summarySubheading}>
                  {t('changeControl.impactSet.summary.byCategory', {
                    defaultValue: 'By category',
                  })}
                </h3>
                <ul className={styles.summaryList}>
                  {CATEGORY_ORDER.map((cat) => {
                    const n = data.summary.by_category[cat] ?? 0;
                    return (
                      <li key={cat} className={styles.summaryRow}>
                        <span>
                          {t(`changeControl.impactSet.category.${cat}.label`, {
                            defaultValue: cat,
                          })}
                        </span>
                        <span className={styles.summaryNum}>{nf.format(n)}</span>
                      </li>
                    );
                  })}
                </ul>

                <h3 className={styles.summarySubheading}>
                  {t('changeControl.impactSet.summary.byConfidence', {
                    defaultValue: 'By confidence',
                  })}
                </h3>
                <ConfidenceBar
                  totals={data.summary.by_confidence}
                  total={totalItems}
                />
                <ul className={styles.confidenceLegend}>
                  {(['HIGH', 'MEDIUM', 'LOW'] as Confidence[]).map((c) => (
                    <li key={c} className={styles.confidenceLegendRow}>
                      <span
                        className={`${styles.legendSwatch} ${
                          c === 'HIGH'
                            ? styles.legendHigh
                            : c === 'MEDIUM'
                              ? styles.legendMedium
                              : styles.legendLow
                        }`}
                        aria-hidden="true"
                      />
                      <span>
                        {t(`changeControl.impactSet.confidence.${c}`, {
                          defaultValue: c,
                        })}
                      </span>
                      <span className={styles.summaryNum}>
                        {nf.format(data.summary.by_confidence[c] ?? 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </aside>
        </div>
      )}

      {/* C-4 — cross-module Related Records (event-linking engine). Only for a
          real change request (the screen's demo default isn't a UUID). */}
      {/^[0-9a-fA-F-]{36}$/.test(changeId) && (
        <RelatedRecords entityType="CHANGE_CONTROL" entityId={changeId} />
      )}

      {dialog.kind === 'acknowledge' && data && (
        <AcknowledgeDialog
          changeId={changeId}
          itemCount={totalItems}
          onClose={() => setDialog({ kind: 'closed' })}
          onAcknowledged={() => {
            setDialog({ kind: 'closed' });
            impactQ.refetch();
          }}
        />
      )}

      {dialog.kind === 'approve' && data && (
        <ApproveDialog
          changeId={changeId}
          itemCount={totalItems}
          onClose={() => setDialog({ kind: 'closed' })}
          onApproved={() => {
            setDialog({ kind: 'closed' });
            impactQ.refetch();
          }}
        />
      )}

      {dialog.kind === 'reject' && (
        <RejectDialog
          changeId={changeId}
          onClose={() => setDialog({ kind: 'closed' })}
          onRejected={() => {
            setDialog({ kind: 'closed' });
            impactQ.refetch();
          }}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * ConfidenceBar — inline-SVG stacked bar with the QWise mint→cyan→blue
 *  gradient family. We hand-roll it (no chart library — see CCIE-001 brief).
 * --------------------------------------------------------------------------- */

interface ConfidenceBarProps {
  totals: Record<Confidence, number>;
  total: number;
}

function ConfidenceBar({ totals, total }: ConfidenceBarProps) {
  const { t } = useTranslation();
  if (total === 0) {
    return (
      <p className={styles.barEmpty}>
        {t('changeControl.impactSet.summary.barEmpty', {
          defaultValue: 'No items to chart.',
        })}
      </p>
    );
  }
  const high = totals.HIGH ?? 0;
  const med = totals.MEDIUM ?? 0;
  const low = totals.LOW ?? 0;
  const W = 100; // percentage units; rendered inside an SVG viewBox
  const H = 8;
  const xHigh = (high / total) * W;
  const xMed = (med / total) * W;
  const xLow = (low / total) * W;

  const summary = t('changeControl.impactSet.summary.barAria', {
    defaultValue:
      'Confidence breakdown — {{h}} high, {{m}} medium, {{l}} low.',
    h: high,
    m: med,
    l: low,
  });

  return (
    <svg
      className={styles.confidenceBar}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={summary}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="conf-bar-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1DD1A1" />
          <stop offset="50%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={xHigh} height={H} fill="url(#conf-bar-grad)" />
      <rect x={xHigh} y={0} width={xMed} height={H} fill="#F59E0B" opacity={0.65} />
      <rect x={xHigh + xMed} y={0} width={xLow} height={H} fill="#9CA3AF" opacity={0.6} />
    </svg>
  );
}
